import React, { useState, useEffect, useRef } from "react";
import {
  faWifi,
  faExclamationTriangle,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "styled-components";
import { Track } from "livekit-client";
import { useSessionContext } from "hooks/useSession";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { colors, constants } from "utils";

const OFFLINE_MESSAGE = "Poor network connection. Your experience may be affected.";

const NetworkIndicatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NetworkIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.color};
  cursor: pointer;
  position: relative;

  &::after {
    content: "${props => props.tooltip}";
    position: absolute;
    top: 50%;
    right: calc(100% + 8px);
    transform: translateY(-50%);
    background-color: ${colors.nightlyWoods500};
    color: ${colors.milkGlass500};
    padding: 6px;
    border-radius: 5px;
    font-size: 16px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
    z-index: 1000;
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;

const SpeedText = styled.span`
  font-size: 9px;
  font-weight: bold;
  color: white;
  margin-top: 1px;
`;

const isNavigatorOnline = () => {
  if (typeof navigator === "undefined") return true;
  // `onLine` only tells us whether a network interface is up — it does NOT
  // guarantee OKO is reachable. Still, `false` is a reliable signal that the
  // device is definitely disconnected, which is what we key off here.
  return navigator.onLine !== false;
};

const getNetworkStatus = (speed, isOffline) => {
  // Offline wins over any cached downlink value — `navigator.connection.downlink`
  // can keep reporting its last reading after the link drops.
  if (isOffline) return { color: "#FF4136", icon: faExclamationTriangle, text: "Offline" };
  if (speed === null || speed === undefined) return { color: "#808080", icon: faExclamationTriangle, text: "Unknown" };
  if (speed <= 1) return { color: "#FF4136", icon: faExclamationTriangle, text: "Poor" };
  if (speed <= 5) return { color: "#FF851B", icon: faWifi, text: "Fair" };
  return { color: "#006B3B", icon: faGlobe, text: "Good" };
};

export const NetworkMonitor = ({ style, updateMessage }) => {
  const { isVideoManuallyMuted } = useAppStateContext();
  const { session, setIsLowBandwidth } = useSessionContext();
  const [navigatorSpeed, setNavigatorSpeed] = useState();
  const [isOffline, setIsOffline] = useState(() => !isNavigatorOnline());
  const lastReportedSpeed = useRef(null);
  const { localTracks, isVideoMuted } = useVideoContext();
  const videoTrack = localTracks.find((track) => track?.source.includes(Track.Source.Camera));

  useEffect(() => {
    const updateConnectionStatus = async () => {
      try {
        // Offline short-circuit — override any downlink reading.
        if (!isNavigatorOnline()) {
          setIsOffline(true);
          setIsLowBandwidth(true);
          if (videoTrack && !isVideoMuted) videoTrack.mute();
          updateMessage(OFFLINE_MESSAGE);
          return;
        }

        setIsOffline(false);

        let downloadSpeedResult = null;
        if (!navigator.connection) {
          // No Network Information API (iPadOS/iOS Safari): connectivity is the
          // only signal we have, so being online must read as a healthy state
          // rather than sticking on "Unknown", and it must never mute video.
          setIsLowBandwidth(false);
          updateMessage("");
        }
        if (navigator.connection) {
          const downlinkSpeed = navigator.connection.downlink;
          downloadSpeedResult = downlinkSpeed.toFixed(1);
          setNavigatorSpeed(downloadSpeedResult);
          if (downloadSpeedResult <= 2) { // Mbps, based on observations when users reported slowness
            setIsLowBandwidth(true);
            if (videoTrack && !isVideoMuted) videoTrack.mute();
            updateMessage("Poor network connection. Your experience may be affected.");
          } else {
            setIsLowBandwidth(false);
            if (videoTrack && !isVideoManuallyMuted && isVideoMuted && session?.sessionActivityType !== constants.CONSENSUS_ENUM)
              videoTrack.unmute();
            updateMessage("");
          }
        }

        if (session && downloadSpeedResult !== lastReportedSpeed.current) {
          await request(`user-data/${session.id}/bandwidth`, {
            payload: {
              navigator_downlink: downloadSpeedResult
            },
          }).then(() => {
            lastReportedSpeed.current = downloadSpeedResult;
          }).catch((e) => {
            console.error("Error in bandwidth API request: ", e);
          });
        }
      } catch (e) {
        console.error("Error in updateConnectionStatus: ", e);
      }
    };

    updateConnectionStatus();
    const intervalId = setInterval(updateConnectionStatus, 60000);
    navigator.connection?.addEventListener("change", updateConnectionStatus);
    // window online/offline fire immediately on link state change — no 60s wait.
    window.addEventListener("online", updateConnectionStatus);
    window.addEventListener("offline", updateConnectionStatus);

    return () => {
      clearInterval(intervalId);
      navigator.connection?.removeEventListener("change", updateConnectionStatus);
      window.removeEventListener("online", updateConnectionStatus);
      window.removeEventListener("offline", updateConnectionStatus);
    };
  }, [isVideoMuted, isVideoManuallyMuted, session, videoTrack, setIsLowBandwidth, updateMessage]);

  // Without the Network Information API (Safari) there is no reading to show:
  // render nothing while online — a bare healthy dot conveys nothing — and only
  // surface the indicator when connectivity is actually lost.
  const hasSpeedApi = typeof navigator !== "undefined" && !!navigator.connection;
  if (!hasSpeedApi && !isOffline) return null;

  const status = getNetworkStatus(navigatorSpeed, isOffline);
  const speedLabel = isOffline ? "0M" : (navigatorSpeed ? `${navigatorSpeed}M` : "?");

  return (
    <NetworkIndicatorContainer style={style} data-testid="network-monitor">
      <NetworkIndicator
        color={status.color}
        tooltip={hasSpeedApi ? `Navigator Bandwidth: ${status.text}` : `Connection: ${status.text}`}
        data-testid="network-indicator"
        data-status={status.text.toLowerCase()}
      >
        <FontAwesomeIcon icon={status.icon} color="white" size="xs" />
        {hasSpeedApi && <SpeedText>{speedLabel}</SpeedText>}
      </NetworkIndicator>
    </NetworkIndicatorContainer>
  );
};
